import { useEffect, useState } from 'react'
import Title from '../../common/Title'
import { useIntl } from 'react-intl'
import Card from '../../common/Card'
import FileUploadButton from '../../common/FileUploadButton'
import { useMutation } from '@tanstack/react-query'
import { importFilePartner } from '../../../api/partner'
import { useGetProgramFilter } from '../../../hooks/useGetProgramFilter'
import { Select } from 'ui/fields'
import useIsProcessingImport from '../../../hooks/useIsProcessingImport/useIsProcessingImport'
import Alert from 'ui/Alert'
import { createDateFromISO } from 'utils/dates'
import { useAppSelector } from '../../../store/hooks'
import { getAlertType } from '../../../utils/partnerUtils'
import useFileUpload from '../../../hooks/useFIleUpload/useFileUpload'
import { toast } from 'react-toastify'
import { Button } from 'ui'

function ImportUsers() {
   const [error, setError] = useState(null)
   const { data: programs, isLoading } = useGetProgramFilter()
   const { data: dataImported, reset, isDone } = useIsProcessingImport({ type: 'MEMBERS' })
   const { locale } = useAppSelector((state) => state.base)
   const intl = useIntl()
   const { mutate: submitForm } = useMutation({
      mutationFn: importFilePartner,
   })
   const onSucces = () => {
      toast.info(intl.formatMessage({ id: 'partner.import.success' }))
      reset()
   }
   const onError = (e: any) => {
      toast.error(intl.formatMessage({ id: 'partner.import.error' }))
   }
   const { showStatusBanner, programId, setProgramId, file, setFile, handleFileChange } = useFileUpload(
      submitForm,
      0,
      onSucces,
      onError,
   )
   useEffect(() => {
      setError(null)
      setFile(null)
   }, [programId, setFile])

   const isLoadingImport =
      dataImported?.result?.status === 'PENDING' || showStatusBanner || dataImported?.result?.status === 'IN_PROGRESS'
if (dataImported && dataImported.result && !isDone) {
      const { status } = dataImported.result
      const isInProgress = status === 'IN_PROGRESS'
      return (
         <div className="px-5 py-2">
            <Card className="p-5">
               <Alert className="m-5" type={getAlertType(status)}>
                  <Alert.Body className="font-semibold flex items-center gap-3">
                     <svg
                        className="animate-spin h-5 w-5 text-blue-500 shrink-0"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                     >
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                     </svg>
                     <div>
                        <div>{isInProgress ? 'Procesando importación...' : 'Importación en cola, espere...'}</div>
                        <div className="text-sm font-normal text-gray-500 mt-1">
                           Este proceso puede tardar unos momentos. No cierre esta ventana.
                        </div>
                     </div>
                  </Alert.Body>
               </Alert>
            </Card>
         </div>
      )
   }
   if (dataImported && dataImported.result && isDone) {
         const { result, message } = dataImported
         const { importDetails, processed, notProcessed, status, createdAt } = result
         const hasImportErrors = importDetails && importDetails.length > 0
         return (
            <div className="px-5 py-2">
               <Card className="p-5">
                  <Alert className={'m-5'} type={getAlertType(status)}>
                     <Alert.Body className="font-semibold">
                        <div>{message}</div>
                        <div>
                           {intl.formatMessage(
                              { id: 'partner.createAt' },
                              {
                                 date: createDateFromISO(createdAt)
                                    .setLocale(intl.locale)
                                    .toFormat("cccc d 'de' LLLL 'del' yyyy"),
                              },
                           )}
                        </div>
                       
                     </Alert.Body>
                  </Alert>
                   <div className="mx-5 mb-4 flex justify-between items-center">
                     {processed !== null && notProcessed !== null && (
                           <div className="mt-2 flex gap-4 text-sm font-normal">
                              <span className="text-green-700">✓ Procesados: {processed}</span>
                              {notProcessed > 0 && (
                                 <span className="text-red-600">✗ No procesados: {notProcessed}</span>
                              )}
                           </div>
                        )}
                     <Button onClick={reset} bsStyle="primary">
                        Nueva importación
                     </Button>
                  </div>
                    {hasImportErrors && (
                     <div className="mx-5 mb-5">
                        <h3 className="font-semibold text-gray-700 mb-3">
                           Detalle de registros no procesados ({importDetails.length})
                        </h3>
                        <div className="overflow-x-auto rounded border border-gray-200">
                           <table className="w-full text-sm">
                              <thead className="bg-gray-50">
                                 <tr>
                                    <th className="px-4 py-3 border-b text-left font-medium text-gray-600">ID</th>
                                    <th className="px-4 py-3 border-b text-left font-medium text-gray-600">External ID</th>
                                    <th className="px-4 py-3 border-b text-left font-medium text-gray-600">Nombre</th>
                                    <th className="px-4 py-3 border-b text-left font-medium text-gray-600">Email</th>
                                    <th className="px-4 py-3 border-b text-left font-medium text-gray-600">Motivo</th>
                                 </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                 {importDetails.map((detail, index) => (
                                    <tr key={detail.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                       <td className="px-4 py-3">{detail.id}</td>
                                       <td className="px-4 py-3">{detail.externalId}</td>
                                       <td className="px-4 py-3">
                                          {detail.firstName || detail.lastName
                                             ? `${detail.firstName ?? ''} ${detail.lastName ?? ''}`.trim()
                                             : <span className="text-gray-400 italic">—</span>}
                                       </td>
                                       <td className="px-4 py-3">
                                          {detail.email ?? <span className="text-gray-400 italic">—</span>}
                                       </td>
                                       <td className="px-4 py-3 text-red-600">{detail.message}</td>
                                    </tr>
                                 ))}
                              </tbody>
                           </table>
                        </div>
                     </div>
                  )}
               </Card>
            </div>
         )
      }
   return (
      <>
         <Title
            className="flex justify-between gap-x-2  items-center "
            value={intl.formatMessage({ id: 'partner.import' })}
         ></Title>
         <div className="flex flex-col gap-y-4 mx-4">
            <Card className="p-5">
               {dataImported && dataImported.result && (
                  <Alert className={'m-5'} type={getAlertType(dataImported.result.status)}>
                     <Alert.Body className="font-semibold">
                        <div>{dataImported?.message}</div>
                        <div>
                           {intl.formatMessage(
                              { id: 'partner.createAt' },
                              {
                                 date: createDateFromISO(dataImported.result.createdAt)
                                    .setLocale(locale)
                                    .toFormat("cccc d 'de' LLLL 'del' yyyy"),
                              },
                           )}
                        </div>
                        <div>{dataImported.errorMessage}</div>
                     </Alert.Body>
                  </Alert>
               )}

               <Select
                  label={intl.formatMessage({ id: 'program.planName' })}
                  value={programId}
                  onChange={setProgramId}
                  showErrorText={true}
                  className="mb-4"
                  disabled={isLoadingImport || isLoading}
               >
                  <Select.Option value="">--</Select.Option>
                  {programs?.items.map((program) => (
                     <Select.Option key={program.id} value={program.id}>
                        {program.name}
                     </Select.Option>
                  ))}
               </Select>
               {!!programId && (
                  <FileUploadButton
                     handleFileChange={handleFileChange}
                     loading={isLoadingImport}
                     file={file}
                     setFile={setFile}
                     error={error}
                     setError={setError}
                     helpText={intl.formatMessage({ id: 'partner.importHelpText' })}
                     loadingLabel="Cargando operaciones..."
                  />
               )}
            </Card>
         </div>
      </>
   )
}

export default ImportUsers
